import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowBigUp, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Pill, Loader } from "@/components/Bits";
import { useAuth } from "@/context/AuthContext";

const DiscussionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [data, setData] = useState<any>(null);
  const [reply, setReply] = useState("");

  const load = () => api.get(`/discussions/${id}`).then((r) => setData(r.data)).catch(() => setData(false));
  useEffect(() => { load(); }, [id]);

  const post = async () => {
    if (!user) return login();
    if (!reply.trim()) return;
    try {
      await api.post(`/discussions/${id}/replies`, { body: reply });
      setReply("");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const upvote = async () => {
    if (!user) return login();
    try {
      await api.post(`/discussions/${id}/upvote`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const removeThread = async () => {
    try {
      await api.delete(`/discussions/${id}`);
      toast.success("Thread removed");
      navigate("/discussions");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const removeReply = async (rid: string) => {
    try {
      await api.delete(`/discussions/replies/${rid}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (data === null) return <Loader />;
  if (data === false) return <div className="mx-auto max-w-[1400px] px-5 py-32 text-white/50" data-testid="discussion-not-found">Thread not found.</div>;

  const d = data.discussion;
  const isAdmin = user?.role === "admin";

  return (
    <div className="mx-auto max-w-3xl px-5 lg:px-10 py-16" data-testid="discussion-detail-page">
      <div className="flex flex-wrap items-center gap-3">
        <Pill tone="blue">{d.topic}</Pill>
        <span className="text-[11px] uppercase tracking-[0.18em] text-white/35">
          {d.author_name} · {new Date(d.created_at).toLocaleDateString("en-IN", { dateStyle: "medium" })}
        </span>
        {isAdmin && (
          <button onClick={removeThread} data-testid="delete-thread-btn" className="ml-auto flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-white/40 hover:text-neon transition-colors">
            <Trash2 size={13} /> Remove
          </button>
        )}
      </div>
      <h1 className="mt-5 font-bebas text-5xl tracking-wide uppercase leading-[0.94]" data-testid="discussion-title">{d.title}</h1>
      <p className="mt-6 whitespace-pre-line text-base text-white/65 leading-relaxed" data-testid="discussion-body">{d.body}</p>

      <div className="mt-8 flex items-center gap-4">
        <button onClick={upvote} data-testid="discussion-upvote-btn"
          className="flex items-center gap-2 border border-white/20 px-5 h-11 text-[12px] font-bold uppercase tracking-[0.18em] hover:border-neon hover:text-neon transition-colors">
          <ArrowBigUp size={16} /> Upvote
        </button>
        <span className="font-bebas text-3xl tracking-wide" data-testid="discussion-upvotes">{d.upvotes}</span>
      </div>

      <div className="mt-14">
        <h3 className="font-bebas text-3xl tracking-wide uppercase">{data.replies.length} replies</h3>
        <div className="mt-6 flex gap-3">
          <input value={reply} onChange={(e) => setReply(e.target.value)} placeholder={user ? "Add to the conversation" : "Sign in to reply"} data-testid="reply-input"
            className="flex-1 h-11 bg-white/[0.04] border border-white/10 px-4 text-sm outline-none focus:border-neon/60 transition-colors" />
          <button onClick={post} data-testid="reply-submit-btn" className="border border-white/20 px-5 text-[12px] font-bold uppercase tracking-[0.18em] hover:border-neon hover:text-neon transition-colors">Reply</button>
        </div>

        <div className="mt-10 grid gap-6">
          {data.replies.map((r: any, i: number) => (
            <div key={r.reply_id} className="border-l-2 border-white/10 pl-5 hover:border-neon transition-colors" data-testid={`reply-${i}`}>
              <div className="flex items-center gap-3 text-sm">
                <span className="font-semibold">{r.user_name}</span>
                <span className="text-[11px] uppercase tracking-widest text-white/30">{new Date(r.created_at).toLocaleDateString("en-IN")}</span>
                {isAdmin && (
                  <button onClick={() => removeReply(r.reply_id)} data-testid={`delete-reply-${i}`} className="ml-auto text-white/30 hover:text-neon transition-colors">
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
              <p className="mt-2 text-sm text-white/60 leading-relaxed">{r.body}</p>
            </div>
          ))}
          {data.replies.length === 0 && <p className="text-sm text-white/35">No replies yet.</p>}
        </div>
      </div>
    </div>
  );
};

export default DiscussionDetail;
